import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { usePageMeta, Field } from "@/components/common";
import { Logo } from "@/components/Layout";

function Shell({ title, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-5 pt-24 pb-16 relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] purple-glow" />
      <div className="relative w-full max-w-md nx-card p-8 md:p-10 fade-up">
        <Logo />
        <h1 className="mt-8 text-2xl font-bold uppercase tracking-tight text-[#F5F5F7]">{title}</h1>
        {children}
      </div>
    </div>
  );
}

export function Login() {
  usePageMeta("Accedi");
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const u = await login(form.email, form.password);
      navigate(["super_admin", "staff"].includes(u.role) ? "/dashboard" : "/area");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Accedi">
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
        <Field label="Email"><input data-testid="login-email-input" type="email" required autoComplete="email" className="nx-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
        <Field label="Password"><input data-testid="login-password-input" type="password" required autoComplete="current-password" className="nx-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></Field>
        {error && <div data-testid="login-error" role="alert" className="text-sm text-red-300">{error}</div>}
        <button data-testid="login-submit-button" disabled={busy} className="btn-primary w-full">{busy ? "Accesso..." : "Accedi"}</button>
      </form>
      <p className="mt-4 text-sm"><Link data-testid="forgot-password-link" to="/password-dimenticata" className="text-[#C6C6CE]/70 hover:text-[#F5F5F7]">Password dimenticata?</Link></p>
      <p className="mt-3 text-sm text-[#C6C6CE]/60">Non hai un account? <Link data-testid="go-register-link" to="/registrazione" className="text-[#962FFF] hover:underline">Registrati</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  usePageMeta("Password dimenticata");
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMsg(data.message);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Password dimenticata">
      {msg ? <p data-testid="forgot-success" role="status" className="mt-6 text-sm text-emerald-300">{msg}</p> : (
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-form">
          <p className="text-sm text-[#C6C6CE]/70">Inserisci l'email del tuo account: ti invieremo un link valido 60 minuti.</p>
          <Field label="Email"><input data-testid="forgot-email-input" type="email" required className="nx-input" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          {error && <div data-testid="forgot-error" role="alert" className="text-sm text-red-300">{error}</div>}
          <button data-testid="forgot-submit" disabled={busy} className="btn-primary w-full">{busy ? "Invio..." : "Invia link"}</button>
        </form>
      )}
      <p className="mt-6 text-sm"><Link to="/login" className="text-[#962FFF] hover:underline">Torna al login</Link></p>
    </Shell>
  );
}

export function ResetPassword() {
  usePageMeta("Reimposta password");
  const navigate = useNavigate();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [pw, setPw] = useState({ a: "", b: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.a !== pw.b) {
      setError("Le password non coincidono.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password: pw.a });
      toast.success("Password aggiornata. Ora puoi accedere.");
      navigate("/login");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Nuova password">
      {!token ? <p data-testid="reset-missing-token" className="mt-6 text-sm text-red-300">Link non valido. <Link to="/password-dimenticata" className="text-[#962FFF] hover:underline">Richiedine uno nuovo</Link>.</p> : (
        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
          <Field label="Nuova password (min. 8 caratteri)"><input data-testid="reset-password-input" type="password" required minLength={8} autoComplete="new-password" className="nx-input" value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} /></Field>
          <Field label="Conferma password"><input data-testid="reset-password-confirm-input" type="password" required minLength={8} autoComplete="new-password" className="nx-input" value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} /></Field>
          {error && <div data-testid="reset-error" role="alert" className="text-sm text-red-300">{error}</div>}
          <button data-testid="reset-submit" disabled={busy} className="btn-primary w-full">{busy ? "Salvataggio..." : "Salva nuova password"}</button>
        </form>
      )}
    </Shell>
  );
}

export function VerifyEmail() {
  usePageMeta("Conferma email");
  const { user, refresh } = useAuth();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [state, setState] = useState(token ? "loading" : "error");
  const [error, setError] = useState(token ? "" : "Link non valido.");
  const started = useRef(false);
  useEffect(() => {
    if (!token || started.current) {
      return;
    }
    started.current = true; // single-use token: avoid a 2nd POST (StrictMode double effect) overwriting success
    api.post("/auth/verify-email", { token })
      .then(() => { setState("ok"); refresh(); })
      .catch((e) => { setState("error"); setError(errMsg(e)); });
  }, [token, refresh]);
  return (
    <Shell title="Conferma email">
      {state === "loading" && <p data-testid="verify-loading" className="mt-6 text-sm text-[#C6C6CE]/70">Verifica in corso...</p>}
      {state === "ok" && <p data-testid="verify-success" role="status" className="mt-6 text-sm text-emerald-300">Indirizzo email confermato. Ora puoi usare tutte le funzioni del tuo account.</p>}
      {state === "error" && <p data-testid="verify-error" role="alert" className="mt-6 text-sm text-red-300">{error}</p>}
      <p className="mt-6 text-sm"><Link data-testid="verify-continue-link" to={user ? "/area" : "/login"} className="text-[#962FFF] hover:underline">{user ? "Vai all'area personale" : "Accedi"}</Link></p>
    </Shell>
  );
}

export function Register() {
  usePageMeta("Registrazione");
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", display_name: "", gamertag: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await register(form);
      toast.success("Account creato. Ti abbiamo inviato un'email per confermare l'indirizzo.");
      navigate("/area");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Crea account">
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
        <Field label="Nome visualizzato"><input data-testid="register-name-input" required minLength={2} className="nx-input" value={form.display_name} onChange={set("display_name")} /></Field>
        <Field label="Gamertag (opzionale)"><input data-testid="register-gamertag-input" className="nx-input" value={form.gamertag} onChange={set("gamertag")} /></Field>
        <Field label="Email"><input data-testid="register-email-input" type="email" required autoComplete="email" className="nx-input" value={form.email} onChange={set("email")} /></Field>
        <Field label="Password (min. 8 caratteri)"><input data-testid="register-password-input" type="password" required minLength={8} autoComplete="new-password" className="nx-input" value={form.password} onChange={set("password")} /></Field>
        {error && <div data-testid="register-error" role="alert" className="text-sm text-red-300">{error}</div>}
        <button data-testid="register-submit-button" disabled={busy} className="btn-primary w-full">{busy ? "Registrazione..." : "Registrati"}</button>
      </form>
      <p className="mt-6 text-sm text-[#C6C6CE]/60">Hai già un account? <Link data-testid="go-login-link" to="/login" className="text-[#962FFF] hover:underline">Accedi</Link></p>
    </Shell>
  );
}
